import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import { List, LayoutGrid, Filter, ChevronDown, SearchX, Loader2 } from 'lucide-react';
import type { Product, Relation, FilterMode } from '../types/product';
import { useProducts } from '../hooks/useProducts';
import { useProductCatalog } from '../hooks/useProductCatalog';
import {
  useTerritories,
  useApplications,
  useIndications,
  useMarketingClaims,
  usePartners,
  useCategories,
} from '../hooks/useMasterData';
import ProductTable from '../components/products/ProductTable';
import ProductCard from '../components/products/ProductCard';
import ProductFilterSidebar, { type FilterModes } from '../components/products/ProductFilterSidebar';
import ProductSearch from '../components/products/ProductSearch';
import SegmentControl from '../components/common/SegmentControl';
import Button from '../components/common/Button';
import Pagination from '../components/common/Pagination';
import { useTranslation } from '../i18n';
import { LIST_PARAMS, MODE_PARAMS, FILTER_PARAM_NAMES } from '../utils/productListParams';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Filters {
  territories: string[];
  applications: string[];
  indications: string[];
  marketingClaims: string[];
  partners: string[];
  categories: string[];
  agreements: string[];
  onlyNew: boolean;
  onlyNewStudy: boolean;
}

export type SortOption =
  | 'relevance'
  | 'name-asc'
  | 'name-desc'
  | 'articleNumber-asc'
  | 'articleNumber-desc'
  | 'partner-asc'
  | 'partner-desc'
  | 'indication-asc'
  | 'indication-desc';

type ViewMode = 'table' | 'grid';

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function listParam(params: URLSearchParams, key: string): string[] {
  const value = params.get(key);
  return value ? value.split(',') : [];
}

function modeParam(params: URLSearchParams, key: string): FilterMode {
  return params.get(key) === 'AND' ? 'AND' : 'OR';
}

function writeParam(params: URLSearchParams, key: string, value: string): void {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}

function baseCompare(a: Product, b: Product, sort: SortOption | null): number {
  if (sort === null) return 0;
  switch (sort) {
    case 'relevance':
      return 0;
    case 'name-asc':
      return a.name.localeCompare(b.name, 'de');
    case 'name-desc':
      return b.name.localeCompare(a.name, 'de');
    case 'articleNumber-asc':
      return a.articleNumber.localeCompare(b.articleNumber);
    case 'articleNumber-desc':
      return b.articleNumber.localeCompare(a.articleNumber);
    case 'partner-asc':
      return (a.partner?.name ?? '').localeCompare(b.partner?.name ?? '', 'de');
    case 'partner-desc':
      return (b.partner?.name ?? '').localeCompare(a.partner?.name ?? '', 'de');
    case 'indication-asc':
      return (a.indications[0]?.name ?? '').localeCompare(b.indications[0]?.name ?? '', 'de');
    case 'indication-desc':
      return (b.indications[0]?.name ?? '').localeCompare(a.indications[0]?.name ?? '', 'de');
  }
}

function sortProducts(
  list: Product[],
  sort: SortOption | null,
): Product[] {
  const sorted = [...list];
  sorted.sort((a, b) => baseCompare(a, b, sort));
  return sorted;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ProductListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const nameParam = searchParams.get('name') ?? '';
  const { t } = useTranslation();

  // ---- View & sort state (default to cards on mobile) ----
  const [view, setView] = useState<ViewMode>(() =>
    window.innerWidth < 1024 ? 'grid' : 'table'
  );
  const [sort, setSort] = useState<SortOption | null>('name-asc');
  const [sortPickedForSearch, setSortPickedForSearch] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const handleSortChange = useCallback((next: SortOption | null) => {
    setSort(next);
    setSortPickedForSearch(true);
  }, []);

  // ---- Filter sidebar visibility (mobile) ----
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ---- Filters, read from the URL on every change ----
  const filters = useMemo<Filters>(
    () => ({
      territories: listParam(searchParams, LIST_PARAMS.territories),
      applications: listParam(searchParams, LIST_PARAMS.applications),
      indications: listParam(searchParams, LIST_PARAMS.indications),
      marketingClaims: listParam(searchParams, LIST_PARAMS.marketingClaims),
      partners: listParam(searchParams, LIST_PARAMS.partners),
      categories: listParam(searchParams, LIST_PARAMS.categories),
      agreements: listParam(searchParams, LIST_PARAMS.agreements),
      onlyNew: searchParams.get('new') === '1',
      onlyNewStudy: searchParams.get('newStudy') === '1',
    }),
    [searchParams],
  );

  const filterModes = useMemo<FilterModes>(
    () => ({
      applications: modeParam(searchParams, MODE_PARAMS.applications),
      indications: modeParam(searchParams, MODE_PARAMS.indications),
      marketingClaims: modeParam(searchParams, MODE_PARAMS.marketingClaims),
    }),
    [searchParams],
  );

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleFilterChange = useCallback(
    (next: Filters) => {
      updateParams((params) => {
        for (const key of Object.keys(LIST_PARAMS) as (keyof typeof LIST_PARAMS)[]) {
          writeParam(params, LIST_PARAMS[key], next[key].join(','));
        }
        writeParam(params, 'new', next.onlyNew ? '1' : '');
        writeParam(params, 'newStudy', next.onlyNewStudy ? '1' : '');
      });
    },
    [updateParams],
  );

  const handleFilterModesChange = useCallback(
    (next: FilterModes) => {
      updateParams((params) => {
        for (const key of Object.keys(MODE_PARAMS) as (keyof FilterModes)[]) {
          writeParam(params, MODE_PARAMS[key], next[key] === 'AND' ? 'AND' : '');
        }
      });
    },
    [updateParams],
  );

  // ---- Local search state, driven by the ?name= param ----
  const [localSearch, setLocalSearch] = useState(nameParam);

  useEffect(() => {
    setLocalSearch(nameParam);
  }, [nameParam]);

  const searchTerm = localSearch.trim();
  const hasSearchTerm = searchTerm.length > 0;

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      updateParams((params) => writeParam(params, 'name', value));
      if (value.trim().length > 0 !== hasSearchTerm) {
        setSortPickedForSearch(false);
      }
    },
    [updateParams, hasSearchTerm],
  );

  const effectiveSort = hasSearchTerm && !sortPickedForSearch ? 'relevance' : sort;

  const SORT_LABELS: Partial<Record<SortOption, string>> = {
    ...(hasSearchTerm ? { relevance: t.productList.sort.relevance } : {}),
    'name-asc': t.productList.sort.nameAsc,
    'name-desc': t.productList.sort.nameDesc,
    'articleNumber-asc': t.productList.sort.articleNumber,
    'partner-asc': t.productList.sort.partner,
    'indication-asc': t.productList.sort.indication,
  };

  // ---- Active filter count ----
  const activeFilterCount =
    filters.territories.length +
    filters.applications.length +
    filters.indications.length +
    filters.marketingClaims.length +
    filters.partners.length +
    filters.categories.length +
    filters.agreements.length +
    (filters.onlyNew ? 1 : 0) +
    (filters.onlyNewStudy ? 1 : 0);

  // ---- Load product catalog (for filter option fallback) ----
  const { data: catalog } = useProductCatalog();

  // ---- Load master data for filter options ----
  const { data: territoriesData } = useTerritories();
  const { data: applicationsData } = useApplications();
  const { data: indicationsData } = useIndications();
  const { data: marketingClaimsData } = useMarketingClaims();
  const { data: partnersData } = usePartners();
  const { data: categoriesData } = useCategories();

  // Extract unique filter options from cached catalog as offline fallback
  const catalogFilterOptions = useMemo(() => {
    if (!catalog || catalog.length === 0) return null;
    const unique = <T extends Relation>(items: T[]) => {
      const map = new Map<number, { id: string; name: string }>();
      for (const item of items) {
        if (item.isActive === false) continue;
        map.set(item.id, { id: String(item.id), name: item.name });
      }
      return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'de'));
    };
    return {
      territories: unique(catalog.flatMap((p) => p.territories)),
      applications: unique(catalog.flatMap((p) => p.applications)),
      indications: unique(catalog.flatMap((p) => p.indications)),
      marketingClaims: unique(catalog.flatMap((p) => p.marketingClaims)),
      partners: unique(catalog.filter((p) => p.partner).map((p) => p.partner!)),
      categories: unique(catalog.filter((p) => p.category).map((p) => p.category!)),
    };
  }, [catalog]);


  const filterOptions = useMemo(() => {
    const toOptions = <T extends { id: number; name: string }>(data: T[] | undefined) =>
      (data ?? []).map((item) => ({ id: String(item.id), name: item.name }));

    const api = {
      territories: toOptions(territoriesData),
      applications: toOptions(applicationsData),
      indications: toOptions(indicationsData),
      marketingClaims: toOptions(marketingClaimsData),
      partners: toOptions(partnersData),
      categories: toOptions(categoriesData),
    };

    // Use API data when available, fall back to catalog-derived options
    const fallback = catalogFilterOptions;
    return {
      territories: api.territories.length > 0 ? api.territories : (fallback?.territories ?? []),
      applications: api.applications.length > 0 ? api.applications : (fallback?.applications ?? []),
      indications: api.indications.length > 0 ? api.indications : (fallback?.indications ?? []),
      marketingClaims: api.marketingClaims.length > 0 ? api.marketingClaims : (fallback?.marketingClaims ?? []),
      partners: api.partners.length > 0 ? api.partners : (fallback?.partners ?? []),
      categories: api.categories.length > 0 ? api.categories : (fallback?.categories ?? []),
    };
  }, [territoriesData, applicationsData, indicationsData, marketingClaimsData, partnersData, categoriesData, catalogFilterOptions]);

  // ---- All filters go to API ----
  const apiParams = useMemo(() => ({
    name: searchTerm || undefined,
    categories: filters.categories.length > 0 ? filters.categories : undefined,
    territories: filters.territories.length > 0 ? filters.territories : undefined,
    applications: filters.applications.length > 0 ? filters.applications : undefined,
    indications: filters.indications.length > 0 ? filters.indications : undefined,
    marketingClaims: filters.marketingClaims.length > 0 ? filters.marketingClaims : undefined,
    partners: filters.partners.length > 0 ? filters.partners : undefined,
    agreements: filters.agreements.length > 0 ? filters.agreements : undefined,
    isNew: filters.onlyNew || undefined,
    hasNewStudy: filters.onlyNewStudy || undefined,
    applicationMode: filterModes.applications,
    indicationMode: filterModes.indications,
    marketingClaimMode: filterModes.marketingClaims,
  }), [searchTerm, filters, filterModes]);

  const { products: allProducts, totalItems, isLoading } = useProducts(apiParams);

  // ---- Sorting (client-side on loaded products) ----
  const sortedProducts = useMemo(
    () => sortProducts(allProducts, effectiveSort),
    [allProducts, effectiveSort],
  );

  // ---- Pagination state ----
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);

  // Reset to first page whenever the filtered/sorted result changes shape.
  useEffect(() => {
    setCurrentPage(1);
  }, [apiParams, effectiveSort, pageSize]);

  const pagedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedProducts.slice(start, start + pageSize);
  }, [sortedProducts, currentPage, pageSize]);

  // ---- Reset filters ----
  const resetFilters = useCallback(() => {
    updateParams((params) => {
      for (const param of FILTER_PARAM_NAMES) {
        params.delete(param);
      }
      params.delete('name');
    });
    setSortPickedForSearch(false);
  }, [updateParams]);

  // ---- Navigation ----
  const handleProductClick = useCallback(
    (id: string) => {
      navigate(`/products/${id}`, { state: { backgroundLocation: location, searchTerm } });
    },
    [navigate, location, searchTerm],
  );

  // ---- Segment control options ----
  const viewOptions = useMemo(
    () => [
      {
        value: 'table',
        label: t.productList.viewTable,
        icon: <List className="w-4 h-4" />,
      },
      {
        value: 'grid',
        label: t.productList.viewCards,
        icon: <LayoutGrid className="w-4 h-4" />,
      },
    ],
    [t.productList.viewTable, t.productList.viewCards],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* ----------------------------------------------------------------- */}
      {/* Top bar                                                           */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Search */}
        <div className="flex-1 max-w-xl">
          <ProductSearch value={localSearch} onChange={handleSearchChange} />
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <SegmentControl
            options={viewOptions}
            value={view}
            onChange={(v) => setView(v as ViewMode)}
          />

          {/* Sort dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setSortOpen(!sortOpen)}
              className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            >
              {effectiveSort ? SORT_LABELS[effectiveSort] ?? t.productList.sort.nameAsc : t.productList.sort.nameAsc}
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${sortOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {sortOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setSortOpen(false)}
                />
                <ul className="absolute right-0 z-20 mt-1 w-48 rounded-2xl bg-white dark:bg-gray-800 shadow-lg py-1">
                  {(Object.keys(SORT_LABELS) as SortOption[]).map((key) => (
                    <li key={key}>
                      <button
                        type="button"
                        className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer ${
                          effectiveSort === key
                            ? 'bg-vivatis-50 text-vivatis-700 dark:bg-vivatis-900/30 dark:text-vivatis-300'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => {
                          handleSortChange(key);
                          setSortOpen(false);
                        }}
                      >
                        {SORT_LABELS[key]}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* Mobile filter toggle */}
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer relative"
          >
            <Filter className="w-4 h-4" />
            <span>{t.common.filter}</span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-vivatis-600 text-white text-[10px] font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Product count */}
      <p role="status" aria-live="polite" className="text-sm text-gray-500 dark:text-gray-400">
        {isLoading
          ? t.productList.loading
          : t.productList.productCount(sortedProducts.length, catalog?.length ?? totalItems)}
        {!isLoading && effectiveSort && (
          <span className="sr-only"> — {SORT_LABELS[effectiveSort] ?? t.productList.sort.nameAsc}</span>
        )}
      </p>

      {/* ----------------------------------------------------------------- */}
      {/* Main layout: sidebar + content                                     */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex gap-6 items-start">
        {/* Filter sidebar */}
        <ProductFilterSidebar
          filters={filters}
          onFilterChange={handleFilterChange}
          filterModes={filterModes}
          onFilterModesChange={handleFilterModesChange}
          filterOptions={filterOptions}
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Loader2 className="w-8 h-8 text-vivatis-400 animate-spin mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {t.productList.loading}
              </p>
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <SearchX className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-1 font-medium break-words max-w-md">
                {hasSearchTerm
                  ? t.common.noResultsFor(searchTerm)
                  : t.common.noProductsFound}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
                {t.productList.adjustFilters}
              </p>
              <Button variant="secondary" onClick={resetFilters}>
                {t.productList.resetFilters}
              </Button>
            </div>
          ) : (
            <>
              {view === 'table' ? (
                <ProductTable
                  products={pagedProducts}
                  onProductClick={handleProductClick}
                  sortOption={effectiveSort}
                  onSortChange={handleSortChange}
                  searchTerm={searchTerm}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
                  {pagedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onClick={() => handleProductClick(String(product.id))}
                      searchTerm={searchTerm}
                    />
                  ))}
                </div>
              )}

              <div className="mt-6">
                <Pagination
                  page={currentPage}
                  pageSize={pageSize}
                  totalItems={sortedProducts.length}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                />
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProductListPage;
